import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
  TextField,
  IconButton,
  Button,
  Menu,
  MenuItem,
  FormHelperText,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PatternFormat } from "react-number-format";

// ---------- Types ----------
type LinkItem = { platform: string; url: string };
type PhoneItem = { countryCode: string; phoneNumber: string };

// Accept optional arrays (backend may omit them). We normalize inside for UI.
export type ContactFormShape = {
  emails?: string[];
  phoneNumbers?: PhoneItem[];
  faxNumber?: string;
  links?: LinkItem[];
};

type ErrorsShape = Record<string, string | undefined>;

type Props = {
  form: ContactFormShape;
  errors: ErrorsShape;
  isFormDisabled: boolean;
  t: (s: string) => string;
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
  socialList: string[];
  handleAddDynamic: (key: "emails" | "phoneNumbers") => void;
  handleDeleteDynamic: (
    key: "emails" | "phoneNumbers" | "links",
    idx: number
  ) => void;
  handleDynamicFieldChange: (
    key: "emails" | "links",
    idx: number,
    value: any
  ) => void;
  handleAddLink: (platform: string) => void;
  handlePhoneNumberChange: (idx: number, digitsOnly: string) => void;
  handleFaxNumberChange: (digitsOnly: string) => void;
  computedPhoneCode: string;
};

// ---------- Helpers ----------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PHONE_DIGITS = 10; // adjust if needed

const isValidEmail = (s: string) => EMAIL_RE.test((s || "").trim());
const digitsOnly = (s: string) => (s || "").replace(/\D/g, "");
const isValidPhone = (digits: string) => digits.length >= MIN_PHONE_DIGITS;

const ensureAtLeastOne = <T,>(arr: T[] | undefined, makeEmpty: () => T): T[] =>
  arr && arr.length > 0 ? arr : [makeEmpty()];

// ---------- Local subcomponent ----------
type PhoneFieldProps = {
  value: string;
  onChange: (digitsOnly: string) => void;
  disabled?: boolean;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
};

/**
 * PatternFormat forwards most props to the customInput (TextField).
 * We pass error and helperText so the TextField renders red + helper like email does.
 */
const PhoneField: React.FC<PhoneFieldProps> = ({
  value,
  onChange,
  disabled = false,
  onBlur,
  error,
  helperText,
}) => (
  <PatternFormat
    // Use a stable, common US pattern (space instead of dash is fine)
    format="(###) ###-####"
    mask="_"
    allowEmptyFormatting={false}
    // CRITICAL: value is digits only + tell the component it's numeric
    value={value}
    valueIsNumericString
    // Limit length *inside* the formatter so the caret doesn't fight you
    isAllowed={(vals) => (vals.value || "").length <= 10}
    onValueChange={(vals) => {
      // vals.value is always digits only
      onChange(vals.value || "");
    }}
    onBlur={onBlur}
    customInput={TextField}
    fullWidth
    label="Phone number"
    // Pass through to the underlying TextField
    error={error}
    helperText={helperText}
    // Mobile keyboard hint
    inputMode="tel"
  />
);
// ---------- Component ----------
const ContactLinksAccordion: React.FC<Props> = ({
  form,
  errors,
  isFormDisabled,
  t,
  anchorEl,
  setAnchorEl,
  socialList,
  handleAddDynamic,
  handleDeleteDynamic,
  handleDynamicFieldChange,
  handleAddLink,
  handlePhoneNumberChange,
  handleFaxNumberChange,
  computedPhoneCode,
}) => {
  // Track touched fields for inline validation
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const markTouched = (k: string) => setTouched((p) => ({ ...p, [k]: true }));

  // Normalize arrays so UI always has at least one row
  const emails = ensureAtLeastOne(form.emails, () => "");
  const phones = ensureAtLeastOne(form.phoneNumbers, () => ({
    countryCode: computedPhoneCode || "",
    phoneNumber: "",
  }));
  const links = form.links ?? [];
  const faxDigits = digitsOnly(form.faxNumber || "");

  // Row-level validation (show errors after touch)
  const emailErrorAt = (i: number): string | undefined => {
    const v = (emails[i] || "").trim();
    const required = i === 0; // first row required
    if (!touched[`emails.${i}`]) return undefined;
    if (required && v.length === 0) return "Email is required";
    if (v.length > 0 && !isValidEmail(v)) return "Enter a valid email";
    return undefined;
  };

  const phoneErrorAt = (i: number): string | undefined => {
    const raw = phones[i]?.phoneNumber || "";
    const d = digitsOnly(raw);
    const required = i === 0; // first row required
    if (!touched[`phone.${i}`]) return undefined;
    if (required && d.length === 0) return "Phone number is required";
    if (d.length > 0 && !isValidPhone(d)) return `Enter at least ${MIN_PHONE_DIGITS} digits`;
    return undefined;
  };

  // Absolute validity (used for header chip; independent of "touched")
  const firstEmailValid = isValidEmail((emails[0] || "").trim());
  const firstPhoneValid = isValidPhone(digitsOnly(phones[0]?.phoneNumber || ""));

  // 3-state header chip like Address:
  // - Complete: both valid
  // - Partial: exactly one valid
  // - Required: neither valid
  const headerState =
    firstEmailValid && firstPhoneValid
      ? "complete"
      : firstEmailValid || firstPhoneValid
      ? "partial"
      : "required";

  return (
    <Accordion defaultExpanded={false} sx={{ mt: 4, borderRadius: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="contact-links-content"
        id="contact-links-header"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <Typography variant="h6">🔗 Contact & Links</Typography>

          {headerState === "complete" && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<CheckCircleRoundedIcon />}
              label="Complete"
            />
          )}

          {headerState === "partial" && (
            <Chip
              size="small"
              color="default"
              variant="outlined"
              icon={<InfoOutlinedIcon />}
              label="Some contact info provided"
            />
          )}

          {headerState === "required" && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              icon={<ErrorOutlineRoundedIcon />}
              label="Contact info required"
            />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Emails */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">{t("Emails")}</Typography>

            {emails.map((em, i) => {
              const err = emailErrorAt(i);
              return (
                <Box key={i} display="flex" gap={1} mb={1} alignItems="flex-start">
                  <TextField
                    fullWidth
                    disabled={isFormDisabled}
                    label={i === 0 ? "Email (required)" : "Email"}
                    value={em}
                    onChange={(e) => handleDynamicFieldChange("emails", i, e.target.value)}
                    onBlur={() => markTouched(`emails.${i}`)}
                    error={Boolean(err)}
                    helperText={err || ""}
                  />
                  {i > 0 && (
                    <IconButton
                      aria-label="delete email"
                      disabled={isFormDisabled}
                      onClick={() => handleDeleteDynamic("emails", i)}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              );
            })}

            <Button
              startIcon={<AddIcon />}
              disabled={isFormDisabled}
              onClick={() => handleAddDynamic("emails")}
            >
              {t("Add Email")}
            </Button>
          </Grid>

          {/* Phone Numbers */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">{t("Phone Numbers")}</Typography>

            {phones.map((p, i) => {
              const err = phoneErrorAt(i);
              return (
                <Box key={i} display="flex" gap={1} mb={1} alignItems="flex-start">
                  <TextField
                    label="Country code"
                    value={computedPhoneCode}
                    disabled
                    sx={{ width: { xs: "35%", sm: "20%" } }}
                  />
                  <Box sx={{ flex: 1 }}>
                  <PhoneField
  value={(p.phoneNumber || "").replace(/\D/g, "")} // ensure digits in case legacy data is masked
  onChange={(digits) => handlePhoneNumberChange(i, digits)}
  disabled={isFormDisabled}
  onBlur={() => markTouched(`phone.${i}`)}
  error={Boolean(err)}
  helperText={err || ""}
/>
                  </Box>

                  {i > 0 && (
                    <IconButton
                      aria-label="delete phone"
                      disabled={isFormDisabled}
                      onClick={() => handleDeleteDynamic("phoneNumbers", i)}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              );
            })}

            <Button
              startIcon={<AddIcon />}
              disabled={isFormDisabled}
              onClick={() => handleAddDynamic("phoneNumbers")}
            >
              {t("Add Phone")}
            </Button>
          </Grid>

          {/* Fax Number */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">{t("Fax Number")}</Typography>
            <Box sx={{ mt: 1, maxWidth: { xs: "100%", sm: 360 } }}>
              <PhoneField
                value={faxDigits}
                onChange={handleFaxNumberChange}
                disabled={isFormDisabled}
                helperText="Optional fax number for inbound/outbound eFax"
              />
            </Box>
          </Grid>

          {/* Links */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t("Links")}
              <Button
                startIcon={<AddIcon />}
                disabled={isFormDisabled}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ ml: 2 }}
              >
                {t("Add Link")}
              </Button>
            </Typography>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              {socialList.map((plat) => (
                <MenuItem key={plat} onClick={() => handleAddLink(plat)}>
                  {plat}
                </MenuItem>
              ))}
            </Menu>

            {links.map((ln, i) => (
              <Box key={i} display="flex" gap={1} mb={1}>
                <TextField label={ln.platform} value={ln.platform} disabled sx={{ width: "30%" }} />
                <TextField
                  fullWidth
                  disabled={isFormDisabled}
                  label="URL"
                  value={ln.url}
                  onChange={(e) =>
                    handleDynamicFieldChange("links", i, { ...ln, url: e.target.value })
                  }
                />
                <IconButton
                  aria-label="delete link"
                  disabled={isFormDisabled}
                  onClick={() => handleDeleteDynamic("links", i)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Grid>
        </Grid>

        {/* Optional global helper when first row invalid and touched */}
        {(touched["emails.0"] || touched["phone.0"]) &&
          (!firstEmailValid || !firstPhoneValid) && (
            <Box sx={{ mt: 1 }}>
              <FormHelperText error>
                Please provide at least one valid email and one valid phone number.
              </FormHelperText>
            </Box>
          )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ContactLinksAccordion;
